class Drawer extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      closeSelector: this.getAttribute('data-close-selector'),
      openSelector: this.getAttribute('data-open-selector')
    }
    
    this.drawerIsOpen = false;
    this.closeTriggers = this.querySelectorAll(this._selectors.closeSelector);
    this.underlay = this.querySelector('[data-drawer-underlay]');
    
    // Bind methods
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.toggleDrawer = this.toggleDrawer.bind(this);
    
    this.init();
  }

  init() {
    // Add event listeners using dynamic selectors
    const openTriggers = document.querySelectorAll(this._selectors.openSelector);
    const drawerId = this.id;
    
    if (openTriggers.length > 0 && drawerId) {
      openTriggers.forEach(trigger => {
        trigger.addEventListener('click', function(evt) {
          // Prevent default focus behavior and blur the trigger
          evt.preventDefault();
          this.blur();
          document.dispatchEvent(new CustomEvent('theme:drawer:open', {
            detail: { drawerId: drawerId }
          }));
        });
      });
    }

    this.closeTriggers?.forEach(trigger => {
      trigger.addEventListener('click', this.closeDrawer);
    });
    this.underlay?.addEventListener('click', this.closeDrawer);
    
    // Keyboard events
    this.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') {
        this.closeDrawer();
      }
    });

    // Listen for custom events - only respond to events for this specific drawer
    document.addEventListener('theme:drawer:open', (event) => {
      if (event.detail?.drawerId === this.id) {
        this.openDrawer();
      }
    });
    document.addEventListener('theme:drawer:close', (event) => {
      if (event.detail?.drawerId === this.id) {
        this.closeDrawer();
      }
    });
    document.addEventListener('theme:drawer:toggle', (event) => {
      if (event.detail?.drawerId === this.id) {
        this.toggleDrawer();
      }
    });
  }

  openDrawer() {
    if (this.drawerIsOpen) return;
    
    this.drawerIsOpen = true;
    this.classList.add('is-open');
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('theme:drawer:opened', {
      detail: { target: this, drawerId: this.id },
      bubbles: true
    }));
    
    // Lock scroll
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    
    // Focus management - use setTimeout to ensure DOM is fully updated
    setTimeout(() => {
      const closeButton = this.querySelector(this._selectors.closeSelector);
      if (closeButton) {
        closeButton.focus();
      } else {
        this.focus();
      }
    }, 0);
  }

  closeDrawer() {
    if (!this.drawerIsOpen) return;
    
    this.classList.add('is-closing');
    this.classList.remove('is-open');
    this.drawerIsOpen = false;
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('theme:drawer:closed', {
      detail: { target: this, drawerId: this.id },
      bubbles: true
    }));
    
    // Unlock scroll
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    
    // Remove closing class after animation
    setTimeout(() => {
      this.classList.remove('is-closing');
    }, 300);
  }

  toggleDrawer() {
    if (this.drawerIsOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }
}

// Register the custom element
if (!customElements.get('s-drawer')) {
  customElements.define('s-drawer', Drawer);
}

// Static methods for external control
Drawer.openById = function(drawerId) {
  document.dispatchEvent(new CustomEvent('theme:drawer:open', {
    detail: { drawerId: drawerId }
  }));
};

Drawer.closeById = function(drawerId) {
  document.dispatchEvent(new CustomEvent('theme:drawer:close', {
    detail: { drawerId: drawerId }
  }));
};

Drawer.toggleById = function(drawerId) {
  document.dispatchEvent(new CustomEvent('theme:drawer:toggle', {
    detail: { drawerId: drawerId }
  }));
};